import { EmployeePaymentMethodType } from '@zorro/clients';
import { Loader } from '@zorro/design-system/v2/Loader';
import { Stack, Text } from '@zorro/web/design-system';
import { useAnalytics } from '@zorro/web/shared-ui';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { ChangeEvent, useState } from 'react';

import { DirectACHDetails } from './DirectACHDetails';
import {
  FALLBACK_PAYMENT_METHOD_DETAILS,
  getPaymentMethodDisplayInfo,
} from './EmployeePaymentMethodDetailsUtils';
import { PaymentCardDetails } from './PaymentCardDetails';

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
  withHeader?: boolean;
};

export const EmployeePaymentMethodDetails = ({
  employeeId,
  onboardingPeriodId,
  withHeader = false,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const [showDetails, setShowDetails] = useState(false);

  const { data: paymentMethodDetails, isLoading } = useMonolithQuery({
    method: 'paymentsControllerEmployeePaymentMethodDetails',
    params: [employeeId, { onboardingPeriodId }],
  });

  const handleToggle = async (event: ChangeEvent<HTMLInputElement>) => {
    setShowDetails(event.currentTarget.checked);
    await sendEvent('toggle_payment_details', {
      onboarding_period_id: onboardingPeriodId,
    });
  };

  const data = paymentMethodDetails ?? FALLBACK_PAYMENT_METHOD_DETAILS;
  const { isPaymentDetailsShown, badgeText, badgeType } =
    getPaymentMethodDisplayInfo(data.state, !!data.creditCard);

  if (isLoading) {
    return (
      <Stack align="center" justify="center" gap="xs">
        <Loader />
        <Text fw={500}>Loading employee payment details...</Text>
      </Stack>
    );
  }

  if (data.type === EmployeePaymentMethodType.SELF_PAY) {
    return null;
  }

  if (data.directACH && isPaymentDetailsShown) {
    return (
      <DirectACHDetails
        directACH={data.directACH}
        withDetails={showDetails}
        onToggle={handleToggle}
        badgeText={badgeText}
        withHeader={withHeader}
      />
    );
  }

  return (
    <PaymentCardDetails
      creditCard={isPaymentDetailsShown ? data.creditCard : undefined}
      badgeType={badgeType}
      badgeText={badgeText}
      withDetails={showDetails}
      onToggle={handleToggle}
      withHeader={withHeader}
    />
  );
};
